// components/help/SearchBar.tsx
'use client'
import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { searchPublic, type PublicSearchArticle } from '@/lib/help/publicArticles'
import { shouldLogNoResult } from '@/lib/help/shouldLogNoResult'
import { sendHelpEvent } from '@/lib/help/telemetry'
import type { Locale } from '@/lib/i18n/config'

export function SearchBar({ articles, locale }: { articles: PublicSearchArticle[]; locale: Locale }) {
  const [q, setQ] = useState('')
  const results = q.trim() ? searchPublic(q, locale, articles) : []

  // Telemetría: registra búsquedas sin resultado tras un debounce de 800ms.
  // `lastLogged` evita reenviar el mismo término consecutivo (cada tecleo
  // reinicia el timer; solo el término estable al pausar dispara el evento).
  const resultCount = results.length
  const lastLogged = useRef<string | null>(null)
  useEffect(() => {
    if (!shouldLogNoResult(q, resultCount)) return
    const term = q.trim()
    const timer = setTimeout(() => {
      if (lastLogged.current === term) return
      lastLogged.current = term
      sendHelpEvent({ event: 'search_no_result', query: term, locale })
    }, 800)
    return () => clearTimeout(timer)
  }, [q, resultCount, locale])

  return (
    <div className="relative">
      <input
        value={q} onChange={(e) => setQ(e.target.value)}
        placeholder={locale === 'es' ? 'Busca: dual pricing, propina, kiosko…' : 'Search: dual pricing, tips, kiosk…'}
        className="w-full rounded-xl border border-[var(--redy-border)] bg-[var(--redy-surface)] px-4 py-3 text-[16px] outline-none focus:border-[var(--redy-red)]"
      />
      {results.length > 0 && (
        <div className="absolute z-20 mt-2 w-full rounded-xl border border-[var(--redy-border)] bg-[var(--redy-surface)] shadow-lg">
          {results.map((a) => (
            <Link key={a.public_slug} href={`/help/${a.public_slug}`} className="block border-b border-[var(--redy-border-l)] px-4 py-3 last:border-0 hover:bg-[var(--redy-warm-white)]">
              <div className="font-semibold text-[var(--redy-charcoal)]">{a.title[locale]}</div>
              <div className="text-[13px] text-[var(--redy-secondary)]">{a.summary[locale]}</div>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
